import { Button, Flex, Input, PasswordInput, Text, Alert } from "@theorgchartdev/storybook";
import { OCLink } from "@/appComponents/Blocks/Utils/OCLink";
import { DataSourceSetupComposite } from "./DataSourceSetupComposite";
import { appstr } from "@coreapp/utils/appstr";
import { appcstr } from "@coreapp/utils/appcstr.js";
import { useState, useEffect } from "react";
import { QAPIKeyGenerateRFC, QAPIKeyRetrieveRFC } from "@coreapp/CoreApp/QAPIKeyGenerateRFC";
import { Globals } from "@coreapp/CoreApp/Globals";
import * as StateLib from "@/Utils/StateLib";
import { useToast, TOAST_STATUS } from "@/providers/ToastProvider";
import { DATA_SOURCES_TEST_PREFIX } from "../Utils";

export const OCAPISetupComposite = ({ config, onChange, index, importType, chartConfig }) => {
  const [showApiKey, setShowApiKey] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [retrievedApiKey, setRetrievedApiKey] = useState("");

  const { addToast } = useToast();
  const currentApiKey = retrievedApiKey || "";
  const sourceId = importType === "import" ? "main" : config.sourceId;

  // Load existing API key on component mount
  useEffect(() => {
    if (!sourceId) {
      setRetrievedApiKey("");
      setHasError(false);
      setErrorMessage("");
      setIsLoadingExisting(false);
      return;
    }

    let ignore = false;

    const loadExistingApiKey = async () => {
      setIsLoadingExisting(true);
      setHasError(false);
      setRetrievedApiKey("");

      try {
        const retrieveRFC = new QAPIKeyRetrieveRFC();
        var wsMgr = StateLib.GetWSMgr();
        retrieveRFC.InitRFC(wsMgr);
        retrieveRFC.SetParams(
          wsMgr.GetAccountName(),
          wsMgr.GetUserName(),
          sourceId,
          chartConfig.ID
        );

        const responsePromise = retrieveRFC.GetPromise();
        Globals._rfcMgr?.QueueRFC(retrieveRFC);

        const response = await responsePromise;
        if (ignore) return;
        const result = JSON.parse(response);

        if (result.success && !result.error) {
          setRetrievedApiKey(result.apiKey || "");
        } else {
          console.error("Failed to retrieve API key:", result.message);
          setHasError(true);
          setErrorMessage(result.message || "Failed to load existing API key");
        }
      } catch (error) {
        if (ignore) return;
        console.error("Error retrieving API key:", error);
        setHasError(true);
        setErrorMessage("Error loading existing API key");
      } finally {
        if (!ignore) setIsLoadingExisting(false);
      }
    };

    loadExistingApiKey();

    return () => {
      ignore = true;
    };
  }, [chartConfig.ID, sourceId]); // Reload when chart or source changes

  const handleGenerateApiKey = async () => {
    if (!sourceId) return;

    setIsGenerating(true);
    setHasError(false);

    try {
      const generateRFC = new QAPIKeyGenerateRFC();
      var wsMgr = StateLib.GetWSMgr();
      generateRFC.InitRFC(wsMgr);
      generateRFC.SetParams(
        wsMgr.GetAccountName(),
        wsMgr.GetUserName(),
        sourceId,
        `Employee Data API - ${importType} integration`,
        chartConfig.ID
      );

      const responsePromise = generateRFC.GetPromise();
      Globals._rfcMgr?.QueueRFC(generateRFC);

      const response = await responsePromise;
      const result = JSON.parse(response);

      if (result.success && !result.error) {
        setRetrievedApiKey(result.apiKey);

        addToast({
          message: appcstr("API key generated successfully"),
          status: TOAST_STATUS.SUCCESS,
          timeoutMS: 5000
        });
      } else {
        console.error("Failed to generate API key:", result.message);
        setHasError(true);
        setErrorMessage(result.message || "Failed to generate API key");

        addToast({
          message: appcstr("Failed to generate API key"),
          status: TOAST_STATUS.ERROR,
          timeoutMS: 5000
        });
      }
    } catch (error) {
      console.error("Error generating API key:", error);
      setHasError(true);
      setErrorMessage("Error generating API key");

      // Show error toast
      addToast({
        message: appcstr("Failed to generate API key"),
        status: TOAST_STATUS.ERROR,
        timeoutMS: 5000
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const isLoading = isLoadingExisting || isGenerating;
  const docLink = "https://docs.theorgchart.com/employee-data-api/getting-started";

  const alertMessage = {
    message: appcstr(
      "View or regenerate the API key used for this integration. If you regenerate the key, remember to update it in your external application to maintain connectivity. ",
      {
        linkText: appcstr("Click here to find documentation.")
      }
    ),
    alertProps: {
      template: appcstr(
        "View or regenerate the API key used for this integration. If you regenerate the key, remember to update it in your external application to maintain connectivity. "
      ),
      replaceDict: {
        linkText: appcstr("Click here to find documentation.")
      },
      formatConfig: {
        linkText: {
          type: "custom",
          onClick: () => window.open(docLink, "_blank"),
          style: { color: "#084298", textDecoration: "underline", cursor: "pointer" }
        }
      }
    }
  };

  return (
    <DataSourceSetupComposite
      config={config}
      onChange={onChange}
      index={index}
      chartConfig={chartConfig}
      showInputControls
      showConfiguration={importType !== "import"}
      showLoadHeaders={importType !== "import"}
      importType={importType}
    >
      <Flex direction="column" gap="24px">
        <Alert
          severity={"info"}
          withBorder
          showIcon
          className={"alert-small-text"}
          alertActions={{ closeAction: () => {} }}
        >
          <Text
            size={"small"}
            style={{ color: "#084298" }}
            dynamic={{
              template: alertMessage.alertProps.template,
              replaceDict: alertMessage.alertProps.replaceDict,
              formatConfig: alertMessage.alertProps.formatConfig
            }}
          >
            {alertMessage.message}
          </Text>
        </Alert>
        <Flex direction={"column"} gap={"8px"}>
          <div style={{ marginBottom: 0 }}>
            <PasswordInput
              passwordMask={!showApiKey}
              label={appcstr("genericapikey1")}
              key={"apiKey"}
              placeholder={
                isLoadingExisting
                  ? appcstr("Loading existing key...")
                  : currentApiKey
                    ? appcstr("API key configured")
                    : appcstr("No API key generated")
              }
              value={currentApiKey}
              type={"text"}
              disabled={true}
              width={"100%"}
            />
          </div>

          {/* Generate API Key Button */}
          <Button
            dataTestId={`${DATA_SOURCES_TEST_PREFIX}-ocapi-generate-key-button`}
            modifier={currentApiKey ? "secondary" : "primary"}
            onClick={handleGenerateApiKey}
            disabled={isLoading || !sourceId}
          >
            {isGenerating
              ? appcstr("Generating...")
              : isLoadingExisting
                ? appcstr("Loading...")
                : currentApiKey
                  ? appcstr("Regenerate API Key")
                  : appcstr("Generate API Key")}
          </Button>
          {/* Error message */}
          {hasError && (
            <Alert
              severity={"error"}
              withBorder
              showIcon
              className={"alert-small-text"}
              alertActions={{ closeAction: () => {} }}
            >
              {errorMessage || appcstr("An error occurred. Please try again.")}
            </Alert>
          )}
        </Flex>
      </Flex>
    </DataSourceSetupComposite>
  );
};

//TODO: Deactivate API key button, also auto-deactivate when the source is removed.
